import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Shield, Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const publicLinks = [
  { to: "/", label: "Home" },
  { to: "/resources", label: "Resources" },
  { to: "/track", label: "Track a Concern" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const dashPath = user && user.role === "employee" ? "/dashboard" : "/admin";

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-bone/80 border-b border-forest-600/10">
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <span className="grid place-items-center h-9 w-9 rounded-xl bg-forest-600 text-bone">
            <Shield className="h-5 w-5" strokeWidth={1.5} />
          </span>
          <span className="leading-tight">
            <span className="block font-serif text-lg text-forest-900 font-semibold">Patria ER CoE</span>
            <span className="block text-[10px] tracking-[0.18em] uppercase text-forest-600 font-bold">Center of Excellence</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {publicLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                location.pathname === l.to ? "text-forest-900 font-semibold" : "text-charcoal-600 hover:text-forest-800"
              }`}
            >
              {l.label}
            </Link>
          ))}
          {user ? (
            <>
              {user.role === "super_admin" && (
                <Button
                  variant="ghost"
                  onClick={() => navigate("/admin/content")}
                  data-testid="nav-content-btn"
                  className="rounded-full text-forest-800 hover:bg-forest-50 ml-1"
                >
                  Content Studio
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() => navigate(dashPath)}
                data-testid="nav-dashboard-btn"
                className="rounded-full text-forest-800 hover:bg-forest-50 ml-1"
              >
                <LayoutDashboard className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Dashboard
              </Button>
              <Button
                variant="ghost"
                onClick={() => { logout(); navigate("/"); }}
                data-testid="nav-logout-btn"
                className="rounded-full text-charcoal-600 hover:bg-bone-100"
              >
                <LogOut className="h-4 w-4" strokeWidth={1.5} />
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              onClick={() => navigate("/login")}
              data-testid="nav-login-btn"
              className="rounded-full text-forest-800 hover:bg-forest-50 ml-1"
            >
              Sign In
            </Button>
          )}
          <Button
            onClick={() => navigate("/report")}
            data-testid="nav-share-concern-btn"
            className="rounded-full bg-terracotta-400 hover:bg-terracotta-500 text-white shadow-md hover:-translate-y-0.5 transition-transform ml-2"
          >
            Share a Concern
          </Button>
        </div>

        <button className="md:hidden p-2 text-forest-800" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X className="h-6 w-6" strokeWidth={1.5} /> : <Menu className="h-6 w-6" strokeWidth={1.5} />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden border-t border-forest-600/10 bg-bone px-5 py-4 space-y-1">
          {publicLinks.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-lg text-charcoal-600 hover:bg-forest-50">
              {l.label}
            </Link>
          ))}
          {user ? (
            <>
              <Link to={dashPath} onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-lg text-charcoal-600 hover:bg-forest-50">Dashboard</Link>
              <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="block w-full text-left px-3 py-2.5 rounded-lg text-charcoal-600 hover:bg-forest-50">Sign out</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-lg text-charcoal-600 hover:bg-forest-50">Sign In</Link>
          )}
          <Button onClick={() => { setOpen(false); navigate("/report"); }} className="w-full rounded-full bg-terracotta-400 hover:bg-terracotta-500 text-white mt-2">Share a Concern</Button>
        </div>
      )}
    </header>
  );
}
